/** @jsx jsx */
import { jsx } from "@emotion/react";
import noop from "lodash/fp/noop";
import { FormEvent } from "react";
import {
  ButtonType,
  StringIndexedDictionary,
} from "../../../../../globalTypes";
import { useSecondaryColor } from "../../../../../hooks/personalization/useColors";
import {
  TableCellValue,
  TableColumns,
  TableRow,
} from "../../../../../requests/table/types";
import {
  ColumnFormat,
  ColumnFormatDict,
} from "../../../../../requests/templates/types";
import Checkbox from "../../../../atoms/inputs/Checkbox";
import Loader from "../../../../atoms/Loader";
import {
  CustomColumn,
  CustomColumnsGenerator,
  ITableRowControls,
} from "../../types";
import { clickableTableRowCss } from "../styles";
import TableDataCell from "./TableDataCell";
import { getControlsCellCss } from "./TableDataCell/styles";
import { sortCellKeysByColumnOrder } from "./utils";

const TableDataRow = ({
  row,
  columns,
  columnFormatDict,
  allowRowSelection,
  allowRowClick = false,
  onRowSelection = noop,
  isSelected,
  TableRowDesktopControls,
  customColumnsToStart = () => [],
  customColumnsToEnd = () => [],
  editMode,
  toggleEditMode,
  isLoading,
  onEditRowReset = noop,
  updateEditRow = noop,
  onCellBlur,
  onRowClickCallback = noop,
  onSubmit = noop,
  refetchRowCallback,
  columnOrderByColumnName,
}: TableDataRowProps) => {
  const secondaryColor = useSecondaryColor();

  if (!row) {
    const colCount = 99999; // [MPO] neotestované na všech browserech!!! Očekávané chování je roztažení jednoho td na celou šířku tabulky
    return (
      <tr>
        <td colSpan={colCount}>
          <Loader size={24} />
        </td>
      </tr>
    );
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    onSubmit(e);
  };

  const showControls = !!TableRowDesktopControls;

  const renderCells = () => {
    const { cells } = row;

    const cellKeys = Object.keys(cells);
    const orderedCellKeys = sortCellKeysByColumnOrder(
      cellKeys,
      columnOrderByColumnName
    );

    return orderedCellKeys.map((cKey) => {
      if (!columns[cKey]) {
        return null;
      }
      const { templateItemId, columnAlias } = columns[cKey];
      const format: ColumnFormat | undefined = columnFormatDict[templateItemId];
      return (
        <TableDataCell
          key={cKey}
          columns={columns}
          format={format?.value}
          columnsFormat={columnFormatDict}
          cKey={cKey}
          row={row}
          textColor={secondaryColor}
          editMode={editMode}
          onInputChange={updateEditRow.bind(null, columnAlias)}
          onBlur={onCellBlur}
          refetchRowCallback={refetchRowCallback}
        />
      );
    });
  };

  const renderCustomColumn = (col: CustomColumn, index: number) => (
    <div
      data-cy="icon-tooltip"
      key={col.columnName ?? `custom-col-${String(index)}`}
      className="tableCol stickyCol"
      css={{ display: "table-cell" }}
    >
      {col?.cell?.cellContent}
    </div>
  );

  const renderControls = () => {
    if (!TableRowDesktopControls) return null;
    if (!row || isLoading) return <Loader size={24} />;
    return (
      <TableRowDesktopControls
        tableRow={row}
        editMode={editMode}
        onEditRowReset={onEditRowReset}
        toggleEditMode={toggleEditMode}
        acceptButtonType={ButtonType.Submit}
      />
    );
  };

  const renderSelectCheckbox = () => {
    const renderContent = () => {
      if (isLoading) return <Loader size={24} />;
      return <Checkbox checked={!!isSelected} onChange={onRowSelection} />;
    };

    return (
      <div
        key="checkbox"
        className="checkboxTd tableCol"
        css={{ display: "table-cell" }}
      >
        {renderContent()}
      </div>
    );
  };

  return (
    <form
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="row"
      id="tableRow"
      data-cy={`row row--${row.keyValue}`}
      onSubmit={handleSubmit}
      className="tableRow"
      css={allowRowClick && clickableTableRowCss}
      onClick={allowRowClick ? onRowClickCallback : noop}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
        return true;
      }}
    >
      {allowRowSelection && renderSelectCheckbox()}
      {customColumnsToStart(row).map(renderCustomColumn)}
      {renderCells()}
      {customColumnsToEnd(row).map(renderCustomColumn)}
      <div
        className="tableCol stickyCol"
        css={getControlsCellCss(showControls)}
      >
        {showControls && renderControls()}
      </div>
    </form>
  );
};
export type UpdateTableRowCell = (
  column: string,
  value: TableCellValue
) => void;

export type TableDataRowProps = {
  row: TableRow | null;
  columns: TableColumns;
  columnFormatDict: ColumnFormatDict;
  columnOrderByColumnName: StringIndexedDictionary<number>;

  allowRowSelection: boolean;
  onRowSelection?: () => void;
  isSelected?: boolean;

  allowRowClick?: boolean;
  onRowClickCallback?: () => void;

  TableRowDesktopControls?: ITableRowControls | null;
  customColumnsToStart?: CustomColumnsGenerator;
  customColumnsToEnd?: CustomColumnsGenerator;

  isLoading?: boolean;

  editMode?: boolean;
  toggleEditMode?: () => void;
  onEditRowReset?: () => void;
  updateEditRow?: UpdateTableRowCell;
  onCellBlur?(): void;
  onSubmit?: (e?: FormEvent<HTMLFormElement>) => void;
  refetchRowCallback?: () => void;
};

export default TableDataRow;
